import type { Router } from 'vue-router';

export type AnalyticsBreadcrumbKind = 'interaction' | 'route' | 'analytics';

export interface AnalyticsBreadcrumb {
  ts: number;
  kind: AnalyticsBreadcrumbKind;
  type: string;
  target?: string | null;
  data?: Record<string, any>;
}

const MAX_BREADCRUMBS = 40;
const breadcrumbs: AnalyticsBreadcrumb[] = [];

export function addBreadcrumb(breadcrumb: AnalyticsBreadcrumb) {
  breadcrumbs.push(breadcrumb);

  if (breadcrumbs.length > MAX_BREADCRUMBS) {
    breadcrumbs.splice(0, breadcrumbs.length - MAX_BREADCRUMBS);
  }
}

export function getBreadcrumbs() {
  return breadcrumbs.slice();
}

function getElementLabel(target: EventTarget | null): string | null {
  if (!target || !(target instanceof Element)) return null;

  if (target.id) return `#${target.id}`;

  const analyticsId = target.getAttribute('data-analytics-id');
  if (analyticsId) return `[data-analytics-id="${analyticsId}"]`;

  const gameId = target.getAttribute('data-game-id');
  if (gameId) return `[data-game-id="${gameId}"]`;

  const name = target.getAttribute('name');
  if (name) return `[name="${name}"]`;

  const classList = Array.from(target.classList).slice(0, 3);
  if (classList.length) return `.${classList.join('.')}`;

  return target.tagName.toLowerCase();
}

function sanitizeRouteParams(params: Record<string, string | string[]>) {
  const sensitiveKeys = new Set(['token']);
  const sanitized: Record<string, string | string[]> = {};

  Object.entries(params || {}).forEach(([key, value]) => {
    if (sensitiveKeys.has(key)) {
      sanitized[key] = '[redacted]';
      return;
    }

    sanitized[key] = value;
  });

  return sanitized;
}

function getRouteTemplatePath(router: Router) {
  const route = router.currentRoute.value;
  const lastMatch = route.matched?.[route.matched.length - 1];

  // Child route records often have relative paths (e.g. "login"), so keep both.
  return {
    name: route.name ? String(route.name) : null,
    template: lastMatch?.path ?? null,
  };
}

export function setupInteractionBreadcrumbTracking(router: Router) {
  if (typeof window === 'undefined') return () => {};

  const onClick = (event: MouseEvent) => {
    addBreadcrumb({
      ts: Date.now(),
      kind: 'interaction',
      type: 'click',
      target: getElementLabel(event.target),
      data: {
        x: event.clientX,
        y: event.clientY,
      },
    });
  };

  const onTouchStart = (event: TouchEvent) => {
    const touch = event.touches?.[0];
    addBreadcrumb({
      ts: Date.now(),
      kind: 'interaction',
      type: 'touchstart',
      target: getElementLabel(event.target),
      data: touch
        ? {
            x: touch.clientX,
            y: touch.clientY,
          }
        : undefined,
    });
  };

  window.addEventListener('click', onClick, true);
  window.addEventListener('touchstart', onTouchStart, {
    capture: true,
    passive: true,
  });

  const removeAfterEach = router.afterEach((to, from) => {
    addBreadcrumb({
      ts: Date.now(),
      kind: 'route',
      type: 'route_change',
      data: {
        from: {
          name: from.name ? String(from.name) : null,
          template: from.matched?.[from.matched.length - 1]?.path ?? null,
          params: sanitizeRouteParams(
            from.params as Record<string, string | string[]>
          ),
        },
        to: {
          name: to.name ? String(to.name) : null,
          template: to.matched?.[to.matched.length - 1]?.path ?? null,
          params: sanitizeRouteParams(
            to.params as Record<string, string | string[]>
          ),
        },
        current: getRouteTemplatePath(router),
      },
    });
  });

  return () => {
    window.removeEventListener('click', onClick, true);
    window.removeEventListener('touchstart', onTouchStart, true);
    removeAfterEach();
  };
}
